import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, ArrowRight, FileText, Users, FileDown } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { API, getAuthHeaders } from '../../utils';

const semLabel = (s) => (s === '2' ? 'الفصل الثاني' : 'الفصل الأول');
const fmt = (v) => v == null ? '' : (v % 1 === 0 ? Math.round(v) : v.toFixed(1));

export default function RubricPrint() {
  const { rubricId } = useParams();
  const navigate = useNavigate();
  const [rubric, setRubric] = useState(null);
  const [gradebooks, setGradebooks] = useState([]);
  const [gbId, setGbId] = useState('');
  const [gb, setGb] = useState(null);
  const [evals, setEvals] = useState({});
  const [mode, setMode] = useState('filled'); // 'filled' | 'blank'
  const [loading, setLoading] = useState(true);
  const [schoolName, setSchoolName] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/rubrics/${rubricId}`, getAuthHeaders()),
      axios.get(`${API}/gradebooks`, getAuthHeaders()),
      axios.get(`${API}/auth/profile`, getAuthHeaders()),
    ]).then(([r, g, p]) => { setRubric(r.data); setGradebooks(g.data); setSchoolName(p.data?.school_name || ''); })
      .catch(() => toast.error('تعذر التحميل'))
      .finally(() => setLoading(false));
  }, [rubricId]);

  useEffect(() => {
    if (!gbId) { setGb(null); setEvals({}); return; }
    Promise.all([
      axios.get(`${API}/gradebooks/${gbId}`, getAuthHeaders()),
      axios.get(`${API}/rubrics/${rubricId}/evaluations?gradebook_id=${gbId}`, getAuthHeaders()),
    ]).then(([g, e]) => {
      setGb(g.data);
      setEvals(Object.fromEntries(e.data.map(ev => [ev.student_id, ev])));
    }).catch(() => toast.error('تعذر تحميل بيانات السجل'));
  }, [gbId, rubricId]);

  // Decide what to render: blank template or per-student filled
  const students = useMemo(() => {
    if (mode === 'blank') return [{ id: 'blank', name: '', section: '', grade: '' }];
    if (!gb) return [];
    return gb.students || [];
  }, [mode, gb]);

  const downloadPdf = async () => {
    const pages = document.querySelectorAll('.print-page');
    if (!pages.length) { toast.error('لا توجد صفحات للتحميل'); return; }
    setDownloading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i], { scale: 2, backgroundColor: '#ffffff' });
        const img = canvas.toDataURL('image/png');
        const w = 210, h = (canvas.height * w) / canvas.width;
        if (i > 0) pdf.addPage();
        pdf.addImage(img, 'PNG', 0, 0, w, Math.min(h, 297));
      }
      pdf.save(`${rubric.title || 'بطاقة تقييم'}.pdf`);
    } catch { toast.error('تعذر إنشاء ملف PDF'); }
    finally { setDownloading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120', color: 'white' }}>
        جارٍ التحميل...
      </div>
    );
  }

  return (
    <>
      {/* Print-only stylesheet */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@400;500;600;700;800;900&family=Noto+Sans+Arabic:wght@300;400;500;600;700;800&display=swap');
        @page { size: A4 portrait; margin: 0; }
        @media print {
          html, body { background: #fff !important; }
          .no-print { display: none !important; }
          .print-page { page-break-after: always; box-shadow: none !important; margin: 0 !important; }
          .print-page:last-child { page-break-after: auto; }
        }
        .print-page {
          width: 210mm; height: 297mm; padding: 8mm 12mm 10mm;
          background: #fff; color: #1a1a1a; font-family: 'Noto Sans Arabic', 'Tajawal', sans-serif;
          margin: 0 auto 12mm; box-shadow: 0 6px 28px rgba(0,0,0,0.35);
          box-sizing: border-box; position: relative; overflow: hidden;
          display: flex; flex-direction: column;
        }
        .print-page > * { flex: 0 0 auto; }
        .print-header { text-align: center; margin-bottom: 3mm; }
        .print-header .moe-logo { width: 18mm; height: 18mm; object-fit: contain; margin: 0 auto 1mm; display: block; }
        .print-header .line-1 { font-weight: 700; font-size: 10.5pt; color: #7A1E1E; font-family: 'Noto Kufi Arabic', 'Noto Sans Arabic', sans-serif; }
        .print-header .line-2 { font-weight: 500; font-size: 8.5pt; color: #444; margin-top: 0.5mm; }
        .print-header .line-3 { font-weight: 900; font-size: 12pt; margin-top: 1mm; font-family: 'Noto Kufi Arabic', 'Noto Sans Arabic', sans-serif; }
        .print-divider { border-top: 2px solid #1a1a1a; margin: 2mm 0 4mm; }
        .print-title { text-align: center; font-weight: 800; font-size: 14pt; margin: 1mm 0 4mm; font-family: 'Noto Kufi Arabic', 'Noto Sans Arabic', sans-serif; }
        .print-meta-row { display: flex; gap: 8mm; margin-bottom: 4mm; font-size: 11.5pt; font-weight: 600; }
        .print-meta-row .field { flex: 1; border-bottom: 1.5px dotted #1a1a1a; padding-bottom: 1mm; }
        .print-meta-row .field span.label { font-weight: 800; }
        .print-meta-row .field span.value { font-weight: 600; margin-right: 2mm; }
        .print-question { border: 1.4px solid #1a1a1a; border-radius: 2mm; padding: 2.5mm 3.5mm; margin-bottom: 4mm; font-size: 11.5pt; font-weight: 500; background: #f7f7f7; }
        .print-question .label { font-weight: 800; }
        .print-page > .print-images { flex: 1 1 0; min-height: 20mm; overflow: hidden; display: flex; flex-wrap: wrap; gap: 2.5mm; justify-content: center; align-content: center; align-items: center; margin-bottom: 3mm; }
        .print-images img { object-fit: contain; border: 1px solid #ccc; border-radius: 1.5mm; background: #fafafa; max-width: 100%; max-height: 100%; }
        .print-images.multi img { max-height: 48%; max-width: 48%; }
        .print-table { width: 100%; border-collapse: collapse; margin-top: 2mm; font-size: 11.5pt; }
        .print-table th, .print-table td { border: 1.4px solid #1a1a1a; padding: 2.6mm 3mm; vertical-align: middle; }
        .print-table th { background: #f0f0f0; font-weight: 800; text-align: center; font-family: 'Noto Kufi Arabic', 'Noto Sans Arabic', sans-serif; }
        .print-table .col-no { width: 10mm; text-align: center; }
        .print-table .col-max { width: 22mm; text-align: center; }
        .print-table .col-score { width: 26mm; text-align: center; font-weight: 800; font-size: 13pt; }
        .print-table .total-row td { background: #f0f0f0; font-weight: 800; font-size: 12.5pt; }
        .print-footer { margin-top: auto; display: flex; gap: 16mm; padding-top: 14mm; font-size: 11pt; }
        .print-footer .sig { flex: 1; }
        .print-footer .sig .label { font-weight: 800; }
        .print-footer .sig .line { border-top: 1.4px dotted #1a1a1a; margin-top: 18mm; }
        .print-table.class-table .name-col { text-align: right; min-width: 50mm; }
        .print-table.class-table th, .print-table.class-table td { padding: 2mm; font-size: 10.5pt; }
      `}</style>

      {/* Top control bar (hidden when printing) */}
      <div className="no-print sticky top-0 z-50 px-5 py-3 flex items-center gap-3 flex-wrap"
        style={{ background: '#0B1120', borderBottom: '1px solid rgba(255,255,255,0.08)' }} dir="rtl">
        <button onClick={() => navigate('/teacher/rubrics')} data-testid="print-back-btn"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold"
          style={{ background: 'rgba(255,255,255,0.06)', color: 'white' }}>
          <ArrowRight className="w-4 h-4" /> رجوع
        </button>

        <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <span className="text-xs font-bold text-white">النمط:</span>
          {[
            ['filled', 'مملوءة لكل طالب', <Users className="w-3.5 h-3.5" key="i1" />],
            ['blank', 'نموذج فارغ', <FileText className="w-3.5 h-3.5" key="i3" />],
          ].map(([v, l, icn]) => (
            <button key={v} onClick={() => setMode(v)} data-testid={`print-mode-${v}`}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold transition-all"
              style={mode === v
                ? { background: 'var(--theme-accent)', color: '#0B1120' }
                : { background: 'transparent', color: 'rgba(255,255,255,0.7)' }}>
              {icn} {l}
            </button>
          ))}
        </div>

        {mode !== 'blank' && (
          <select value={gbId} onChange={e => setGbId(e.target.value)} data-testid="print-gb-select"
            className="input-field text-sm font-bold"
            style={{ width: 'auto', maxWidth: '280px' }}>
            <option value="">— اختر الصف/الشعبة —</option>
            {gradebooks.map(g => (
              <option key={g.id} value={g.id}>الصف {g.grade}/{g.section} ({g.student_count} طالب)</option>
            ))}
          </select>
        )}

        <button onClick={() => window.print()} data-testid="print-trigger-btn"
          className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all hover:scale-[1.02]"
          style={{ background: '#34D399', color: '#0B1120' }}>
          <Printer className="w-4 h-4" /> طباعة / حفظ PDF
        </button>
        <button onClick={downloadPdf} disabled={downloading} data-testid="print-download-pdf-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all hover:scale-[1.02] disabled:opacity-60"
          style={{ background: 'rgba(255,255,255,0.08)', color: 'white', border: '1px solid rgba(255,255,255,0.15)' }}>
          <FileDown className="w-4 h-4" /> {downloading ? 'جارٍ التجهيز...' : 'تحميل PDF'}
        </button>
      </div>

      <div className="py-6" style={{ background: '#1E2A3A', minHeight: '100vh' }} dir="rtl">
        {mode !== 'blank' && !gb && (
          <div className="no-print text-center py-20" style={{ color: 'rgba(255,255,255,0.7)' }}>
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-bold mb-1">اختر الصف/الشعبة من الأعلى</p>
            <p className="text-xs">سيظهر النموذج هنا للمعاينة قبل الطباعة</p>
          </div>
        )}

        {mode === 'blank' && (
          <PrintCard rubric={rubric} student={null} evaluation={null} isBlank schoolName={schoolName} />
        )}

        {mode === 'filled' && gb && (gb.students || []).map(st => (
          <PrintCard key={st.id} rubric={rubric} student={st} evaluation={evals[st.id]}
            grade={gb.grade} section={gb.section} schoolName={schoolName} />
        ))}
      </div>
    </>
  );
}

function PrintCard({ rubric, student, evaluation, isBlank, grade, section, schoolName }) {
  const scores = evaluation?.scores || {};
  const studentName = isBlank ? '' : (student?.name || '');
  const gradeStr = isBlank ? '' : `${grade || ''}/${section || ''}`;

  return (
    <div className="print-page">
      <Header schoolName={schoolName} />
      <div className="print-divider" />
      <div className="print-title">{rubric.title}</div>

      <div className="print-meta-row">
        <div className="field">
          <span className="label">الاسم:</span>
          <span className="value">{studentName}</span>
        </div>
        <div className="field" style={{ flex: '0 0 40mm' }}>
          <span className="label">الصف:</span>
          <span className="value">{gradeStr}</span>
        </div>
        <div className="field" style={{ flex: '0 0 40mm' }}>
          <span className="label">الفصل:</span>
          <span className="value">{semLabel(rubric.semester)}</span>
        </div>
      </div>

      {rubric.question && (
        <div className="print-question">
          <span className="label">السؤال:</span> {rubric.question}
        </div>
      )}

      {rubric.images?.length > 0 && (
        <div className={`print-images ${rubric.images.length === 1 ? 'single' : 'multi'}`}>
          {rubric.images.slice(0, 4).map((url, i) => <img key={i} src={url} alt="" />)}
        </div>
      )}

      <table className="print-table">
        <thead>
          <tr>
            <th className="col-no">م</th>
            <th>المعيار / المهارة</th>
            <th className="col-max">الدرجة القصوى</th>
            <th className="col-score">الدرجة</th>
          </tr>
        </thead>
        <tbody>
          {rubric.criteria.map((c, i) => (
            <tr key={c.id}>
              <td className="col-no">{i + 1}</td>
              <td>{c.name}</td>
              <td className="col-max">{fmt(c.max)}</td>
              <td className="col-score">{isBlank ? '' : (scores[c.id] != null ? fmt(scores[c.id]) : '')}</td>
            </tr>
          ))}
          <tr className="total-row">
            <td colSpan={2} style={{ textAlign: 'left' }}>المجموع الكلّي</td>
            <td className="col-max">{fmt(rubric.total_max)}</td>
            <td className="col-score">{isBlank ? '' : (evaluation?.total != null ? fmt(evaluation.total) : '')}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Header({ schoolName }) {
  return (
    <div className="print-header">
      <img src="/moe-logo.jpeg" alt="وزارة التعليم" className="moe-logo" />
      <div className="line-1">سلطنة عمان — وزارة التعليم</div>
      <div className="line-2">المديرية العامة للتعليم بمحافظة شمال الشرقية</div>
      <div className="line-3">{schoolName || 'مدرسة الخيرات للبنين للصفوف ٥-٨'}</div>
    </div>
  );
}
